import clsx from 'clsx';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { TableCell } from './TableCell';
import { TableHeader } from './TableHeader';
import { TableRow } from './TableRow';
import { TableSection } from './TableSection';
import { TableContext } from './TableContext';
import { TableCaption } from './TableCaption';
import { callMultiple, flattenChildren, responsiveCssProperties, useComposedRefs, useControlledValue, useElementSize, useFocusScope, useResponsiveProp, useStableCallback, } from '../../utils';
import { Box, getBoxRestrictedProperties } from '../Box';
import { TableGroup } from './TableGroup';
import { TableEmptyMessage } from './TableEmptyMessage';
import { TableLink } from './TableLink';
import { useKeyboardEvent } from '../../utils/useKeyboardEvent';
import './Table.css';
var KeyboardActions;
(function (KeyboardActions) {
    KeyboardActions[KeyboardActions["FIRST"] = 0] = "FIRST";
    KeyboardActions[KeyboardActions["LAST"] = 1] = "LAST";
    KeyboardActions[KeyboardActions["NEXT"] = 2] = "NEXT";
    KeyboardActions[KeyboardActions["PREVIOUS"] = 3] = "PREVIOUS";
})(KeyboardActions || (KeyboardActions = {}));
export function Table(props) {
    const { children, className, cols, grid, gridRows, gridAreas, tableMinWidth, selectable, selected: userSelected, onChange, onSelect: userOnSelect, onSelectAll: userOnSelectAll, emptyMessage, emptyMessageTitle, onKeyDown: userOnKeyDown, UNSAFE_tableWrapperStyle, style, ...rest } = props;
    const [selected, setSelected] = useControlledValue({
        value: userSelected,
        onChange,
        defaultValue: [],
    });
    const [tableScrollable, setTableScrollable] = useState(false);
    const tableRef = useRef(null);
    const [tableWrapperElement, setTableWrapperElement] = useState(null);
    const tableWrapperRef = useRef(null);
    const [tableBackgroundColor, setTableBackgroundColor] = useState(null);
    const raf = useRef(null);
    const selectableValues = useRef(new Set()).current;
    const hadKeyboardEvent = useKeyboardEvent();
    useElementSize(tableRef, (size) => {
        var _a;
        const width = size.width;
        setTableScrollable(Math.ceil(width) > (((_a = tableWrapperRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) || 0));
    });
    useElementSize(tableWrapperElement, (size) => {
        var _a;
        const width = size.width;
        setTableScrollable((((_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) || 0) > Math.ceil(width));
        if (raf.current) {
            window.cancelAnimationFrame(raf.current);
        }
        raf.current = window.requestAnimationFrame(() => {
            setTableBackgroundColor(getBackgroundColor(tableWrapperRef.current));
        });
    });
    const responsiveGrid = useResponsiveProp(grid);
    const responsiveGridAreas = useResponsiveProp(gridAreas);
    const responsiveGridRows = useResponsiveProp(gridRows);
    const stableUserOnSelect = useStableCallback(userOnSelect);
    const stableUserOnSelectAll = useStableCallback(userOnSelectAll);
    const onSelect = useCallback((value) => {
        setSelected((selected) => {
            if (selected.indexOf(value) > -1) {
                return selected.filter((selectedValue) => selectedValue !== value);
            }
            else {
                return [...selected, value];
            }
        });
        stableUserOnSelect(value);
    }, [setSelected, stableUserOnSelect]);
    const onSelectAll = useCallback(() => {
        setSelected((selected) => {
            if (selected.length === selectableValues.size) {
                return [];
            }
            else {
                return [...selectableValues];
            }
        });
        stableUserOnSelectAll();
    }, [selectableValues, setSelected, stableUserOnSelectAll]);
    const responsiveCols = useResponsiveProp(cols);
    const matchedColumns = responsiveCols === null || responsiveCols === void 0 ? void 0 : responsiveCols.match(/([a-zA-Z0-9-.(%,\s]+\(([^()]|\)[,\s])+\)+)|[a-z-A-Z0-9-%.]+/g);
    const colgroup = matchedColumns ? (React.createElement("colgroup", null, matchedColumns.map((size, index) => {
        return React.createElement("col", { key: index, style: { width: size } });
    }))) : null;
    const allSelected = useMemo(() => selected.length > 0 && selected.length >= selectableValues.size, [selectableValues.size, selected.length]);
    const tableContextValue = useMemo(() => ({
        selected,
        selectable: !!selectable,
        selectableValues,
        tableRef,
        allSelected,
        onSelect,
        onSelectAll,
        hadKeyboardEvent,
    }), [
        selected,
        selectable,
        selectableValues,
        allSelected,
        onSelect,
        onSelectAll,
        hadKeyboardEvent,
    ]);
    const responsiveTableMinWidth = useResponsiveProp(tableMinWidth);
    const [boxRestrictedProperties, tableProps] = getBoxRestrictedProperties(rest);
    const composedTableWrapperRef = useComposedRefs(setTableWrapperElement, tableWrapperRef);
    const nodeFilter = (node) => node instanceof HTMLElement &&
        node.hasAttribute('data-table-row-tabbable') &&
        node.offsetParent != null;
    const focusScope = useFocusScope(tableRef, undefined, nodeFilter);
    const onKeyDown = (e) => {
        var _a;
        if (e.defaultPrevented)
            return;
        if (!((_a = focusScope.getRootNode()) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement)) ||
            !nodeFilter(document.activeElement)) {
            return;
        }
        const { key } = e;
        const actionKey = (() => {
            if (key === 'Home')
                return KeyboardActions.FIRST;
            if (key === 'End')
                return KeyboardActions.LAST;
            if (key === 'ArrowDown')
                return KeyboardActions.NEXT;
            if (key === 'ArrowUp')
                return KeyboardActions.PREVIOUS;
            return;
        })();
        switch (actionKey) {
            case KeyboardActions.FIRST:
                e.preventDefault();
                focusScope.focusFirstDescendant();
                break;
            case KeyboardActions.LAST:
                e.preventDefault();
                focusScope.focusLastDescendant();
                break;
            case KeyboardActions.NEXT:
                e.preventDefault();
                focusScope.focusNextDescendant(document.activeElement);
                break;
            case KeyboardActions.PREVIOUS:
                e.preventDefault();
                focusScope.focusNextDescendant(document.activeElement, true);
                break;
        }
    };
    if (flattenChildren(children).length === 0) {
        // @ts-expect-error data-testid is not recognized on props
        const { 'data-testid': testId, id, className } = props;
        return (React.createElement(Table.EmptyMessage, { title: emptyMessageTitle, id: id, className: className, "data-testid": testId }, emptyMessage));
    }
    return (React.createElement(TableContext.Provider, { value: tableContextValue },
        React.createElement(Box.Restricted, { ...boxRestrictedProperties, className: clsx('mollie-ui-table-wrapper', {
                'mollie-ui-table-wrapper--grid': responsiveGrid,
                [`${className}-wrapper`]: className,
                'mollie-ui-table-wrapper--has-scrollarea': tableScrollable,
            }, className), style: {
                '--mollie-ui-table-background-color': tableBackgroundColor,
                ...UNSAFE_tableWrapperStyle,
            }, ref: composedTableWrapperRef },
            React.createElement("table", { ...tableProps, onKeyDown: callMultiple(userOnKeyDown, onKeyDown), className: clsx('mollie-ui-table', {
                    'mollie-ui-table--fixed': responsiveCols,
                    'mollie-ui-table--grid': responsiveGrid,
                }, className), style: {
                    ...style,
                    ...responsiveCssProperties({
                        '--mollie-ui-table-grid-columns': responsiveGrid,
                        '--mollie-ui-table-grid-rows': responsiveGridRows,
                        '--mollie-ui-table-grid-areas': responsiveGridAreas,
                    }),
                    minWidth: responsiveTableMinWidth,
                }, ref: tableRef, "data-has-selected": selectable ? selected.length > 0 : undefined },
                colgroup,
                children))));
}
Table.EmptyMessage = TableEmptyMessage;
Table.Caption = TableCaption;
Table.Row = TableRow;
Table.Header = TableHeader;
Table.Cell = TableCell;
Table.Group = TableGroup;
Table.Head = createTableSection('thead', 'Table.Head');
Table.Body = createTableSection('tbody', 'Table.Body');
Table.Foot = createTableSection('tfoot', 'Table.Foot');
Table.Link = TableLink;
function createTableSection(elementType, displayName) {
    function TableSectionComponent(props) {
        return React.createElement(TableSection, { ...props, elementType: elementType });
    }
    TableSectionComponent.displayName = displayName;
    return TableSectionComponent;
}
function getBackgroundColor(element) {
    if (!element)
        return null;
    const defaultStyle = getDefaultBackground(); // typically "rgba(0, 0, 0, 0)"
    const backgroundColor = window.getComputedStyle(element).backgroundColor;
    if (backgroundColor !== defaultStyle)
        return backgroundColor;
    if (!element.parentElement)
        return defaultStyle;
    return getBackgroundColor(element.parentElement);
}
function getDefaultBackground() {
    // have to add to the document in order to use getComputedStyle
    const div = document.createElement('div');
    document.head.appendChild(div);
    const bg = window.getComputedStyle(div).backgroundColor;
    document.head.removeChild(div);
    return bg;
}
//# sourceMappingURL=Table.js.map